import { Button, Grid, useMediaQuery, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import cs from 'classnames';
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Permissions } from '../../../../../shared/auth/constants/permissions';
import { useCurrentUser, useHasOneOfPermissions, useHasPermission } from '../../../../Session';
import {
    UserType,
    FullSaleTransactionDataFragment,
    SaleTransactionStage,
    SalesTransactionAction,
    SettingValueUnit,
} from '../../../../api';
import Accordion from '../../../../components/Accordion';
import ContentWithLabel from '../../../../components/ContentWithLabel';
import useCommonStyles from '../../../../useCommonStyles';
import useFormatCurrency from '../../../../utilities/useFormatCurrency';
import { useCancelFormatter } from './ActionCancelTransaction';
import ActionCloseTransaction from './ActionCloseTransaction';
import BiddingBlock from './BiddingBlock';
import ErrorDialog from './ErrorDialog';
import PastBids from './PastBids';
import SelectBidDialog from './SelectBidDialog';
import TopBids from './TopBids';

export type BiddingTabTabProps = {
    saleTransaction: FullSaleTransactionDataFragment;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        changeStageGridItem: {
            [theme.breakpoints.up('sm')]: {
                order: 1,
            },
        },
        closeTransactionLabel: {
            color: '#A5A5A5',
        },

        buttonContainer: {
            [theme.breakpoints.down('md')]: {
                width: '100%',
                flexDirection: 'column',
                paddingLeft: 16,
                paddingRight: 16,
            },
        },
    })
);

const BiddingTab = ({ saleTransaction }: BiddingTabTabProps) => {
    const [openSelectAwardee, setOpenSelectAwardee] = useState(false);
    const [selectAwardeeRequireFields, setSelectAwardeeRequireFields] = useState<string[] | null>(null);

    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const commonStyles = useCommonStyles();
    const currentUser = useCurrentUser();
    const styles = useStyles();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const useCardDesignForAccordion = !isSmall;
    const formatCurrency = useFormatCurrency();
    const canUpdateAwardedOffer = useHasPermission(Permissions.OFFER_UPDATE_AWARDED_OFFER);
    const canSubmitBid = useHasPermission(Permissions.OFFER_SUBMIT_BID);
    const canManageBiddingSession = useHasOneOfPermissions([
        Permissions.OFFER_UPDATE_AWARDED_OFFER,
        Permissions.OFFER_END_OFFER_SESSION,
    ]);
    const canViewPastBids = useHasPermission(Permissions.OFFER_VIEW_PAST_BIDS);

    const checkValidToSelectAwardee = useCallback(() => {
        let isValidToSelectAwardee = true;
        const fields: string[] = [];

        if (!saleTransaction.handover.targetHandoverDateTime) {
            isValidToSelectAwardee = false;
            fields.push(t('saleTransactionsPage:handover.targetHandoverDate'));
            fields.push(t('saleTransactionsPage:handover.targetHandoverTime'));
        }

        if (isValidToSelectAwardee) {
            setOpenSelectAwardee(true);
            setSelectAwardeeRequireFields(null);
        } else {
            setSelectAwardeeRequireFields(fields);
        }
    }, [saleTransaction.handover.targetHandoverDateTime, t]);

    const { latestBiddingSession, latestBiddingSessionEntry } = saleTransaction;

    const lastBid = latestBiddingSessionEntry?.lastUserBid?.amount || null;

    const showSelectAwardee = saleTransaction.stage === SaleTransactionStage.ForAwarding && canUpdateAwardedOffer;
    const isPurschedByPorschePreowned = saleTransaction.action === SalesTransactionAction.Purchased;
    const isCancelled = saleTransaction.stage === SaleTransactionStage.Cancelled;
    const displaySubmittedDealerBid = canSubmitBid;

    const showTopBids = ![SaleTransactionStage.PendingApproval].includes(saleTransaction.stage);

    const isNotDealer = currentUser.type !== UserType.Dealer;
    const { lastActivity } = saleTransaction;

    const handlingCharges = useMemo(() => {
        if (saleTransaction.awardedBiddingEntry) {
            return saleTransaction.tradeInCalculation
                ? `${t('common:currency')}${saleTransaction.tradeInCalculation?.handlingCharges.toLocaleString()}`
                : t('common:blankValue');
        }

        return saleTransaction.handlingCharges?.unit === SettingValueUnit.Amount
            ? formatCurrency(saleTransaction.handlingCharges.value)
            : t('saleTransactionsPage:transactionDetails.yetToBeAwarded');
    }, [
        formatCurrency,
        saleTransaction.awardedBiddingEntry,
        saleTransaction.handlingCharges?.unit,
        saleTransaction.handlingCharges?.value,
        saleTransaction.tradeInCalculation,
        t,
    ]);

    const { formatReasonWithMessage } = useCancelFormatter();

    return (
        <Grid spacing={2} container>
            {showTopBids && (
                <TopBids saleTransaction={saleTransaction} useCardDesignForAccordion={useCardDesignForAccordion} />
            )}
            {isPurschedByPorschePreowned && isNotDealer && (
                <Grid xs={12} item>
                    <Accordion
                        title={t('saleTransactionsPage:sessionDetails.porscheAwarded')}
                        useCardDesign={useCardDesignForAccordion}
                        defaultExpanded
                    >
                        <Typography className={commonStyles.fontWeightLight}>{t('common:porschePreowned')}</Typography>
                    </Accordion>
                </Grid>
            )}
            {isCancelled && isNotDealer && (
                <Grid xs={12} item>
                    <Accordion
                        title={t('saleTransactionsPage:sessionDetails.cancel.title')}
                        useCardDesign={useCardDesignForAccordion}
                        defaultExpanded
                    >
                        <Typography className={commonStyles.fontWeightLight}>
                            {lastActivity.__typename === 'SaleCancelActivity'
                                ? formatReasonWithMessage(lastActivity.reason, lastActivity.reasonMessage)
                                : ''}
                        </Typography>
                    </Accordion>
                </Grid>
            )}
            {!isPurschedByPorschePreowned && (
                <Grid xs={12} item>
                    <Accordion
                        title={t('saleTransactionsPage:sessionDetails.title')}
                        useCardDesign={useCardDesignForAccordion}
                        defaultExpanded
                    >
                        <Grid spacing={1} container>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={latestBiddingSession?.urgent ? t('common:yes') : t('common:no')}
                                    label={t('saleTransactionsPage:sessionDetails.urgent')}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={
                                        latestBiddingSession
                                            ? t('common:formats.longDateTime', {
                                                  date: new Date(saleTransaction.latestBiddingSession.startPeriod),
                                              })
                                            : t('common:blankValue')
                                    }
                                    label={t('saleTransactionsPage:sessionDetails.bidOpeningOn')}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={
                                        latestBiddingSession
                                            ? t('common:formats.longDateTime', {
                                                  date: new Date(saleTransaction.latestBiddingSession.endPeriod),
                                              })
                                            : t('common:blankValue')
                                    }
                                    label={t('saleTransactionsPage:sessionDetails.bidClosingDate')}
                                />
                            </Grid>
                            {displaySubmittedDealerBid && lastBid && (
                                <Grid xs={12} item>
                                    <ContentWithLabel
                                        content={lastBid ? formatCurrency(lastBid) : t('common:blankValue')}
                                        label={t('saleTransactionsPage:bidDrawer.lastBid')}
                                    />
                                </Grid>
                            )}
                            {displaySubmittedDealerBid && (
                                <>
                                    <Grid xs={12} item>
                                        <ContentWithLabel
                                            content={handlingCharges}
                                            label={t('saleTransactionsPage:transactionDetails.handlingCharges')}
                                        />
                                    </Grid>
                                    <Grid xs={12} item>
                                        <ContentWithLabel
                                            content={
                                                saleTransaction.tradeInCalculation?.totalPaymentAmount != null
                                                    ? formatCurrency(
                                                          saleTransaction.tradeInCalculation?.totalPaymentAmount
                                                      )
                                                    : t('common:blankValue')
                                            }
                                            label={t('saleTransactionsPage:transactionDetails.totalPaymentAmount')}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid xs={12} item>
                                <ContentWithLabel
                                    content={
                                        !saleTransaction.latestBiddingSession?.notes
                                            ? t('common:blankValue')
                                            : saleTransaction.latestBiddingSession.notes
                                    }
                                    display="block"
                                    label={t('saleTransactionsPage:sessionDetails.bidNotes')}
                                />
                            </Grid>
                        </Grid>
                    </Accordion>
                </Grid>
            )}
            {showTopBids && <BiddingBlock transaction={saleTransaction} />}
            {canManageBiddingSession && (
                <>
                    <Grid xs={12} item>
                        <Grid className={styles.buttonContainer} spacing={1} container>
                            {showSelectAwardee && (
                                <Grid item>
                                    <Button
                                        className={cs(commonStyles.removeBoxShadow, commonStyles.defaultBorderRadius)}
                                        color="primary"
                                        onClick={checkValidToSelectAwardee}
                                        size="large"
                                        variant="contained"
                                    >
                                        {t('saleTransactionsPage:sessionDetails.selectAwardee')}
                                    </Button>
                                </Grid>
                            )}
                            {[SaleTransactionStage.BiddingOpen, SaleTransactionStage.UpcomingBidding].includes(
                                saleTransaction.stage
                            ) && (
                                <Grid item>
                                    <ActionCloseTransaction saleTransaction={saleTransaction} />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <ErrorDialog
                        fields={selectAwardeeRequireFields}
                        onClose={() => setSelectAwardeeRequireFields(null)}
                    />

                    <SelectBidDialog
                        onClose={() => setOpenSelectAwardee(false)}
                        open={openSelectAwardee}
                        transaction={saleTransaction}
                    />
                </>
            )}
            {canViewPastBids && (
                <Grid xs={12} item>
                    <PastBids saleTransaction={saleTransaction} />
                </Grid>
            )}
        </Grid>
    );
};

export default BiddingTab;
